import React, { FC } from 'react';
import { useTranslation } from 'Shared/Hooks/useTranslation';
import styled from 'styled-components';
import { useQuery } from '@apollo/react-hooks';

import Text from 'crehana-ui/Text';
import Loader from 'crehana-ui/Loader';
import Avatar from 'crehana-ui/Avatar';
import ArrowBottom from 'crehana-icons/ArrowBottom';
import { baseLighter } from 'crehana-ui/styles/colors';

import StringParser from 'Jsx/Shared/StringParser';

import ProgressLine from './ProgressLine';
import GET_REVIEWS_QUERY from './graphql/getReviews.graphql';

import StarsRating from '../../ui/StarsRating';
import { Score } from '../../types';
import locales from '../../i18n/localeEs';

const translations = locales.es;

const REVIEWS_PER_GROUP = 4;

const getSortedScore = scoreList => {
  const scoreSortModel = ['five', 'four', 'three', 'two', 'one'];

  return scoreSortModel.map(el => {
    return scoreList[el];
  });
};

const ReviewsResume = styled.div`
  ${({ theme }) => theme.breakpoints.up('lg')} {
    max-width: 400px;
  }
`;

const ReviewsSection: FC<{
  courseId: number;
  averageScore: Score;
  featuredReviewId: number;
}> = ({ courseId, averageScore, featuredReviewId }) => {
  const { t } = useTranslation();
  const [loadingMoreReviews, setLoadingMoreReviews] = React.useState(false);
  const { data, loading, error, fetchMore } = useQuery(GET_REVIEWS_QUERY, {
    variables: { id: courseId, first: REVIEWS_PER_GROUP },
  });

  const sortedScore = getSortedScore(averageScore.details);

  if (loading || error) {
    return null;
  }

  const reviews = data?.reviews;

  const ReviewsList =
    reviews?.edges?.length > 0 ? (
      <ul>
        {reviews.edges
          .map(el => el.node)
          .filter(el => el.originalId !== featuredReviewId)
          .filter(el => el.comment.length > 20)
          .map(review => (
            <li
              key={`review-${review.originalId}`}
              className="pb-16 mb-16 lg:pr-72 lg:mb-24 lg:pb-24"
              style={{ borderBottom: '1px solid #DFE1E6' }}
            >
              <div className="flex items-start">
                <Avatar
                  className="flex-shrink-0 mr-12 sm:hidden"
                  alt="estudiante"
                  bgColor="black"
                  size={32}
                  src={review.user.thumbnailUrl}
                />
                <Avatar
                  className="flex-shrink-0 mr-12 hidden sm:block"
                  alt="estudiante"
                  bgColor="black"
                  size={40}
                  src={review.user.thumbnailUrl}
                />
                <div>
                  <Text type="subtitle3" as="p">
                    {review.user.username}
                  </Text>
                  <StarsRating
                    size={12}
                    score={Math.floor(review.score)}
                    total={5}
                  />
                </div>
              </div>
              <StringParser string={review.comment} breaks links>
                {({ string }) => (
                  <Text className="mt-8" type="body3" as="p">
                    {string}
                  </Text>
                )}
              </StringParser>
            </li>
          ))}
      </ul>
    ) : null;

  const AverageSummary = (
    <ReviewsResume className="mb-40 lg:flex lg:items-center">
      <div className="mb-24 flex items-center lg:block lg:mr-40 lg:flex-shrink-0">
        <Text type="h1" as="p" responsive="none">
          {averageScore.average.toFixed(1)}
        </Text>
        <div className="ml-16 lg:ml-0">
          <StarsRating
            className="mb-4"
            score={Math.floor(averageScore.average)}
            size={16}
            total={5}
          />
          <Text color="gray-dark" type="legals" as="p">
            {t('TOTAL_SCORE', {
              defaultValue: translations.TOTAL_SCORE,
            })}
          </Text>
        </div>
      </div>
      <ul className="sm:w-6/12 lg:w-full lg:flex-grow">
        {sortedScore.map((score, index) => (
          <li key={`score${index}`} className="flex items-center mb-8">
            <div className="mr-16 flex-grow">
              <ProgressLine progress={score} />
            </div>
            <StarsRating
              className="mr-8"
              score={sortedScore.length - index}
              size={16}
              total={sortedScore.length}
            />
            <Text
              type="body4"
              className="flex-shrink-0"
              style={{ width: 24 }}
              as="p"
            >
              {score}%
            </Text>
          </li>
        ))}
      </ul>
    </ReviewsResume>
  );

  const hasNextPage = data?.reviews?.pageInfo?.hasNextPage;

  return (
    <section className="mb-40 mt-32 lg:mt-48">
      <Text className="mb-16 sm:mb-24" type="h4" as="h4">
        {t('REVIEWS_TITLE', {
          defaultValue: translations.REVIEWS_TITLE,
        })}
      </Text>
      {AverageSummary}
      {ReviewsList}
      {hasNextPage && (
        <>
          {loadingMoreReviews ? (
            <div
              className="w-full flex justify-center items-center"
              style={{ height: 56 }}
            >
              <Loader size={40} />
            </div>
          ) : (
            <button
              type="button"
              className="lg:mt-8 w-full justify-start pl-0 font-gil font-bold text-base-lighter "
              disabled={loadingMoreReviews}
              onClick={() => {
                if (data.reviews.pageInfo.hasNextPage) {
                  setLoadingMoreReviews(true);
                  fetchMore({
                    variables: {
                      after: data.reviews.pageInfo.endCursor,
                    },
                    updateQuery: (prev, { fetchMoreResult, ...rest }) => {
                      setLoadingMoreReviews(false);
                      if (!fetchMoreResult) return prev;
                      return {
                        reviews: {
                          ...fetchMoreResult.reviews,
                          edges: [
                            ...prev.reviews.edges,
                            ...fetchMoreResult.reviews.edges,
                          ],
                        },
                      };
                    },
                  }).catch(err => {
                    console.error(err);
                    setLoadingMoreReviews(false);
                  });
                }
              }}
            >
              <p className="flex items-center font-bold">
                <span className="mr-8">
                  {t('LOAD_MORE_REVIEWS', {
                    defaultValue: translations.LOAD_MORE_REVIEWS,
                  })}
                </span>
                <ArrowBottom size={14} color={baseLighter} />
              </p>
            </button>
          )}
        </>
      )}
    </section>
  );
};

export default ReviewsSection;
